// Számla-szerkesztő űrlap (full-page, create + edit) — react-hook-form + a @atlas/contracts writable mezői
// (zodResolver a features/invoices/form.ts sémájával). A form-INSTANCE-t a route birtokolja (a 422 → mező-hiba
// a mutációból form.setError-rel írható); ez a komponens prezentációs + a form-belső interakciók (tétel-tábla,
// ÁFA-lock, profil-előkitöltés, footer-automatika, élő összeg-előnézet) gazdája.
//
// ÉLŐ ELŐNÉZET: minden tétel net/áfa/bruttó + a fej-összegek a @atlas/shared kanonikus fixpontos
// (calculateInvoiceItem / sumInvoiceTotals) függvényéből — ugyanaz, amit a szerver számol → az előnézet a
// szerver-eredmény. A kliens ÖSSZEGET NEM küld a wire-be (a wire-builder a form.ts-ben él).
//
// A LAP NYELVE (user-döntés): a szerkesztő UGYANAZ a form-oldal, mint az ELŐNÉZET
// (`invoices.$id.index`) — `FormPage` (balra igazított, keskeny hasáb, saját görgetéssel) + `FormSection`-ök:
// NINCS kártya-keret és külön háttér, a tagolást a szekció-cím (`overline`) és a térköz adja. Korábban
// `Card`-ok rácsa volt, ami a szerkesztést és a megtekintést két KÜLÖNBÖZŐ felületnek mutatta ugyanarról a
// bizonylatról. A szekció-sorrend a részletes nézeté: Címzett · Bizonylat · Dátumok · Vevő-adatok · Tételek ·
// Megjegyzés.
//
// ÁFA-LOCK: egy számlán egy áfa-kulcs. Az ELSŐ tétel kulcsa zárolja a többi sor kulcs-mezőjét (disabled +
// az érték szinkronban tartva); egyetlen tételnél felold. A szerver a végállapotot úgyis validálja (422).
//
// KATALÓGUS-CSOMAG (user-döntés): szabad szöveges tétel nincs — minden sor a szolgáltatás-katalógusból választ
// (kereshető választó), és a választás előtölti a nevet, a megjegyzést, az ÁFA-kulcsot és fix áras csomagnál az
// egységárat (`servicePlanPrefill`); mind átírható. A generált (hozzárendelésből jött) sor csomagja zárolt.
import { type InvoiceRefsSchema } from '@atlas/contracts';
import {
  INVOICE_CURRENCIES,
  INVOICE_DELIVERY_TYPES,
  INVOICE_PAYMENT_METHODS,
  INVOICE_QUANTITY_UNITS,
  INVOICE_TYPES,
  INVOICE_VAT_RATES,
} from '@atlas/shared';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { type TFunction } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { type z } from 'zod';

import { EntityMultiSelect } from '@/components/shared/entity-multiselect';
import {
  DateField,
  SearchableSelectField,
  SegmentedField,
  SelectField,
  TextField,
  TextareaField,
} from '@/components/shared/form-fields';
import { FormSection } from '@/components/shared/form-section';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Form } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  addDaysIso,
  emptyInvoiceItem,
  previewItemAmounts,
  previewTotals,
  servicePlanPrefill,
  type InvoiceFormValues,
} from '@/features/invoices/form';
import { formatMoney } from '@/lib/format-money';

export type InvoiceRefs = z.infer<typeof InvoiceRefsSchema>;

interface InvoiceFormProps {
  form: UseFormReturn<InvoiceFormValues>;
  refs: InvoiceRefs | undefined;
  mode: 'create' | 'edit';
  pending?: boolean;
  onSubmit: (values: InvoiceFormValues) => void;
  t: TFunction;
}

export function InvoiceForm({
  form,
  refs,
  mode,
  pending = false,
  onSubmit,
  t,
}: InvoiceFormProps): JSX.Element {
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });
  const formRef = useRef<HTMLFormElement>(null);

  // ÉRVÉNYTELEN mentés SOSEM néma: a nem fókuszálható mezők (pl. a fiók-választó) hibájához az RHF nem tud
  // odaugrani, így a gomb „nem csinált semmit" (éles lelet). Toast + görgetés az ELSŐ kiemelt hibához.
  const onInvalid = (): void => {
    toast.error(t('toast:invoices.validation'));
    requestAnimationFrame(() => {
      formRef.current
        ?.querySelector('[aria-invalid="true"], [data-field-error]')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };

  const currency = form.watch('currency');
  const items = form.watch('items');
  const scopeKind = form.watch('scopeKind');
  const clientId = form.watch('clientId');

  /**
   * ── DÍJBEKÉRŐ → MINDIG PAPÍR ─────────────────────────────────────────────────────────────────────────
   *
   * A díjbekérő a Számlázz.hu-nál mindig papír alapú (nem is számla, tehát e-számla sem lehet). Ha a
   * kolléga mégis „elektronikus"-ra hagyja, a felület olyat ígér, ami nem történik meg — és épp a
   * tévesztést akarjuk megelőzni (user-lelet).
   *
   * A mező ilyenkor ÉRTÉKBEN is papírra áll, nem csak tiltva van: a tiltott select értéke különben
   * elmenne a bizonylattal, és a tárolt adat mást mondana, mint a felület.
   */
  const invoiceKind = form.watch('type');
  const proformaPaperLock = invoiceKind === 'proforma';
  useEffect(() => {
    if (proformaPaperLock && form.getValues('deliveryType') !== 'paper') {
      form.setValue('deliveryType', 'paper', { shouldDirty: true });
    }
  }, [proformaPaperLock, form]);

  // ── ÁFA-lock: >1 tétel → az első tétel kulcsa zárolja a többit (érték-szinkron + a select disable-je). ──
  const multipleItems = items.length > 1;
  const lockedRate = multipleItems ? items[0]?.vatRate : undefined;
  useEffect(() => {
    if (!lockedRate) return;
    items.forEach((it, i) => {
      if (i > 0 && it.vatRate !== lockedRate) {
        form.setValue(`items.${i}.vatRate`, lockedRate, { shouldDirty: true });
      }
    });
  }, [items, lockedRate, form]);

  // ── Jogszabályi klauzula: EUFAD37/FAA kulcsnál a megjegyzéshez fűzve (szerkeszthető marad). ──
  const effRate = items[0]?.vatRate;
  const prevRateRef = useRef(effRate);
  useEffect(() => {
    if (effRate === prevRateRef.current) return;
    prevRateRef.current = effRate;
    if (effRate === 'EUFAD37' || effRate === 'FAA') {
      const legal = t(`invoices.footerLegal.${effRate}`);
      const current = form.getValues('note');
      if (!current.includes(legal)) {
        form.setValue('note', current.trim() === '' ? legal : `${current}\n${legal}`, {
          shouldDirty: true,
        });
      }
    }
  }, [effRate, form, t]);

  // ── Billing-profil előkitöltés (nem tárolt) — a snapshot-mezők + due-default + a megjegyzés. ──
  const [profileId, setProfileId] = useState('');
  const applyProfile = (id: string): void => {
    setProfileId(id);
    const profile = refs?.billingProfiles.find((p) => p.id === id);
    if (!profile) return;
    const set = (name: keyof InvoiceFormValues, value: string): void =>
      form.setValue(name, value, { shouldDirty: true });
    if (profile.billingName) set('billingName', profile.billingName);
    set('billingTaxNumber', profile.taxNumber ?? '');
    set('billingEuTaxNumber', profile.euTaxNumber ?? '');
    set('billingCountryCode', profile.countryCode ?? '');
    set('billingZipCode', profile.zipCode ?? '');
    set('billingCity', profile.city ?? '');
    set('billingAddress', profile.address ?? '');
    set('billingEmails', profile.billingEmails.join(', '));
    // A profil alapértelmezett megjegyzése a számla megjegyzésébe (a bizonylat EGY megjegyzés-blokkot nyomtat).
    if (profile.defaultInvoiceNote) set('note', profile.defaultInvoiceNote);
    // due-default = issue + a profil fizetési határideje (nincs profil-érték → 8 nap).
    set(
      'dueDate',
      addDaysIso(form.getValues('issueDate'), profile.defaultInvoicePaymentDeadlineDays ?? 8),
    );
  };

  // ── Opció-listák (enum → i18n címke) ──
  const enumOptions = (
    values: readonly string[],
    prefix: string,
  ): { value: string; label: string }[] =>
    values.map((v) => ({ value: v, label: t(`invoices.${prefix}.${v}`) }));
  const typeOptions = enumOptions(INVOICE_TYPES, 'typeLabel');
  const deliveryOptions = enumOptions(INVOICE_DELIVERY_TYPES, 'deliveryTypeLabel');
  const paymentOptions = enumOptions(INVOICE_PAYMENT_METHODS, 'paymentMethodLabel');
  const currencyOptions = INVOICE_CURRENCIES.map((c) => ({ value: c, label: c }));
  const unitOptions = enumOptions(INVOICE_QUANTITY_UNITS, 'quantityUnitLabel');
  const vatOptions = enumOptions(INVOICE_VAT_RATES, 'vatRateLabel');
  // A kibocsátó-választó a SZÁMLATÖMB KULCSÁT mutatja („MDIG" · „MOR" · „Nincs"), NEM a cégnevet — ahogy a
  // legacy CRM `InvoicePrefixEnum::forDropdown()` is. Miért: a `none` és a `MOR` kibocsátó cégneve AZONOS
  // (MORGENS Design Kft.), tehát névvel két megkülönböztethetetlen opció állt egymás mellett (user-lelet).
  // A teljes kibocsátó-adat (név, cím, adószám) a választó ALATTI előnézetben látszik.
  const issuerOptions = (refs?.issuers ?? []).map((i) => ({
    value: i.key,
    label: t(`invoices.issuerLabel.${i.key}`, { defaultValue: i.key }),
  }));
  const clientOptions = (refs?.clients ?? []).map((c) => ({ value: c.id, label: c.name }));
  const accountOptions = (refs?.accounts ?? []).map((a) => ({ value: a.id, label: a.name }));
  // A tétel-választó: „Szolgáltatás · Csomag" (a kereső mindkettőre illeszt).
  const servicePlans = refs?.servicePlans ?? [];
  const planById = new Map(servicePlans.map((p) => [p.id, p]));
  const planOptions = servicePlans.map((p) => ({
    value: p.id,
    label: p.serviceName !== '' ? `${p.serviceName} · ${p.planName}` : p.planName,
  }));
  /** A sor választó-opciói: ha a sor csomagja azóta kikerült a katalógusból, az is megjelenik (nem üres mező). */
  const planOptionsFor = (planId: string): { value: string; label: string }[] =>
    planId === '' || planById.has(planId)
      ? planOptions
      : [{ value: planId, label: t('invoices.item.servicePlanUnavailable') }, ...planOptions];

  // A „számlázás módja" SEMLEGES dimenzió (egy fiók vs. összevont ügyfél-számla), nem státusz — ezért
  // `Segmented` és nem `PillField`: a telített, márka-színű pill „elsődleges akció" hangsúlyt vitt egy
  // egyszerű választásra (user-döntés — a szolgáltatás-hozzárendelés sidebarjával egyezően).
  const scopeOptions = [
    { value: 'client', label: t('invoices.scope.client') },
    { value: 'account', label: t('invoices.scope.account') },
  ];

  const clientName = clientOptions.find((c) => c.value === clientId)?.label ?? clientId;
  const selectedIssuer = refs?.issuers.find((i) => i.key === form.watch('issuerKey'));
  const totals = previewTotals(currency, items);

  return (
    <Form {...form}>
      <form ref={formRef} onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-8">
        {/* 1. Címzett — ügyfél + számlázás módja + fiók(ok) */}
        <FormSection title={t('invoices.form.sectionScope')}>
          {mode === 'create' ? (
            <SearchableSelectField<InvoiceFormValues>
              name="clientId"
              label={t('invoices.form.client')}
              required
              placeholder={t('invoices.form.clientPlaceholder')}
              options={clientOptions}
            />
          ) : (
            <div>
              <p className="text-sm font-medium">{t('invoices.form.client')}</p>
              <p className="text-muted-foreground mt-1 text-sm">{clientName}</p>
            </div>
          )}
          <SegmentedField<InvoiceFormValues>
            name="scopeKind"
            label={t('invoices.form.scope')}
            options={scopeOptions}
          />
          {scopeKind === 'account' ? (
            <SearchableSelectField<InvoiceFormValues>
              name="accountId"
              label={t('invoices.form.account')}
              required
              placeholder={t('invoices.form.accountPlaceholder')}
              options={accountOptions}
            />
          ) : (
            <div className="space-y-1.5">
              <p className="text-sm font-medium">
                {t('invoices.form.accounts')}
                <span className="text-destructive"> *</span>
              </p>
              <EntityMultiSelect
                options={refs?.accounts ?? []}
                value={form.watch('accountIds')}
                onChange={(ids) => form.setValue('accountIds', ids, { shouldDirty: true })}
                placeholder={t('invoices.form.accountsPlaceholder')}
                countLabel={(n) => t('invoices.form.accountsCount', { count: n })}
                aria-label={t('invoices.form.accounts')}
              />
              {form.formState.errors.accountIds && (
                <p className="text-destructive text-sm" data-field-error>
                  {form.formState.errors.accountIds.message}
                </p>
              )}
            </div>
          )}
        </FormSection>

        {/* 2. Bizonylat — kibocsátó + típus/kézbesítés/fizetés/deviza */}
        <FormSection title={t('invoices.form.sectionMeta')}>
          <div className="grid gap-5 sm:grid-cols-2">
            <SelectField<InvoiceFormValues>
              name="issuerKey"
              label={t('invoices.form.issuer')}
              required
              options={issuerOptions}
            />
            <SelectField<InvoiceFormValues>
              name="type"
              label={t('invoices.form.type')}
              required
              options={typeOptions}
            />
            <SelectField<InvoiceFormValues>
              name="deliveryType"
              label={t('invoices.form.deliveryType')}
              required
              options={deliveryOptions}
              disabled={proformaPaperLock}
              {...(proformaPaperLock ? { hint: t('invoices.form.proformaPaperHint') } : {})}
            />
            <SelectField<InvoiceFormValues>
              name="currency"
              label={t('invoices.form.currency')}
              required
              options={currencyOptions}
            />
          </div>
          {/* Fizetési mód: SEMLEGES, kis elemszámú dimenzió → szegmentált választó (nem legördülő): a négy
                opció egy pillantással látszik, és egy kattintás a váltás. */}
          <SegmentedField<InvoiceFormValues>
            name="paymentMethod"
            label={t('invoices.form.paymentMethod')}
            required
            options={paymentOptions}
          />
          {selectedIssuer && (selectedIssuer.name ?? selectedIssuer.taxNumber) && (
            <div className="border-border text-muted-foreground rounded-lg border p-3 text-xs">
              {selectedIssuer.name && (
                <div className="text-foreground font-medium">{selectedIssuer.name}</div>
              )}
              {selectedIssuer.taxNumber && <div>{selectedIssuer.taxNumber}</div>}
              {(selectedIssuer.zipCode ?? selectedIssuer.city ?? selectedIssuer.address) && (
                <div>
                  {[selectedIssuer.zipCode, selectedIssuer.city, selectedIssuer.address]
                    .filter(Boolean)
                    .join(' ')}
                </div>
              )}
            </div>
          )}
        </FormSection>

        {/* 3. Dátumok — teljesítés / kiállítás / fizetési határidő */}
        <FormSection title={t('invoices.form.sectionDates')}>
          <div className="grid gap-5 sm:grid-cols-3">
            <DateField<InvoiceFormValues>
              name="fulfillmentDate"
              label={t('invoices.form.fulfillmentDate')}
              required
            />
            <DateField<InvoiceFormValues>
              name="issueDate"
              label={t('invoices.form.issueDate')}
              required
            />
            <DateField<InvoiceFormValues>
              name="dueDate"
              label={t('invoices.form.dueDate')}
              required
            />
          </div>
        </FormSection>

        {/* 4. Vevő-adatok (snapshot) — a billing-profil előkitöltéssel */}
        <FormSection
          title={t('invoices.form.sectionBilling')}
          action={
            refs && refs.billingProfiles.length > 0 ? (
              <div className="w-56">
                <ProfileSelect
                  value={profileId}
                  onChange={applyProfile}
                  placeholder={t('invoices.form.billingProfile')}
                  options={refs.billingProfiles.map((p) => ({
                    value: p.id,
                    label: p.billingName ?? p.id,
                  }))}
                />
              </div>
            ) : null
          }
        >
          <TextField<InvoiceFormValues>
            name="billingName"
            label={t('invoices.form.billingName')}
            required
            autoComplete="off"
          />
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField<InvoiceFormValues>
              name="billingTaxNumber"
              label={t('invoices.form.billingTaxNumber')}
              autoComplete="off"
            />
            <TextField<InvoiceFormValues>
              name="billingEuTaxNumber"
              label={t('invoices.form.billingEuTaxNumber')}
              autoComplete="off"
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-4">
            <TextField<InvoiceFormValues>
              name="billingCountryCode"
              label={t('invoices.form.billingCountryCode')}
              required
              autoComplete="off"
            />
            <TextField<InvoiceFormValues>
              name="billingZipCode"
              label={t('invoices.form.billingZipCode')}
              required
              autoComplete="off"
            />
            <div className="sm:col-span-2">
              <TextField<InvoiceFormValues>
                name="billingCity"
                label={t('invoices.form.billingCity')}
                required
                autoComplete="off"
              />
            </div>
          </div>
          <TextField<InvoiceFormValues>
            name="billingAddress"
            label={t('invoices.form.billingAddress')}
            required
            autoComplete="off"
          />
          <TextField<InvoiceFormValues>
            name="billingEmails"
            label={t('invoices.form.billingEmails')}
            hint={t('invoices.form.billingEmailsHint')}
            autoComplete="off"
          />
        </FormSection>

        {/* 5. Tételek — inline szerkesztő élő sor- és fej-összegekkel */}
        <FormSection
          title={t('invoices.form.sectionItems')}
          action={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append(emptyInvoiceItem(lockedRate ?? items[0]?.vatRate ?? 'VAT_27'))}
            >
              <PlusIcon className="h-4 w-4" />
              {t('invoices.form.addItem')}
            </Button>
          }
        >
          {form.formState.errors.items?.message && (
            <p className="text-destructive text-sm" data-field-error>
              {form.formState.errors.items.message}
            </p>
          )}
          <div className="space-y-4">
            {fields.map((field, index) => {
              const preview = previewItemAmounts(currency, items[index] ?? field);
              const vatLocked = multipleItems && index > 0;
              return (
                <div
                  key={field.id}
                  className="border-border relative rounded-lg border p-4"
                  data-testid={`invoice-item-${String(index)}`}
                >
                  <div className="grid gap-4 sm:grid-cols-12">
                    <div className="sm:col-span-12">
                      <SearchableSelectField<InvoiceFormValues>
                        name={`items.${index}.servicePlanId`}
                        label={t('invoices.item.servicePlan')}
                        placeholder={t('invoices.item.servicePlanPlaceholder')}
                        required
                        disabled={items[index]?.planLocked === true}
                        {...(items[index]?.planLocked === true
                          ? { hint: t('invoices.item.servicePlanLockedHint') }
                          : {})}
                        options={planOptionsFor(items[index]?.servicePlanId ?? '')}
                        onValueChange={(planId) => {
                          const plan = planById.get(planId);
                          if (plan === undefined) return;
                          const patch = servicePlanPrefill(
                            form.getValues(`items.${index}`),
                            plan,
                            currency,
                            vatLocked,
                          );
                          for (const [key, value] of Object.entries(patch)) {
                            form.setValue(
                              `items.${index}.${key as 'name' | 'note' | 'vatRate' | 'unitPrice'}`,
                              value as string,
                              { shouldDirty: true, shouldValidate: form.formState.isSubmitted },
                            );
                          }
                        }}
                      />
                    </div>
                    <div className="sm:col-span-12">
                      <TextField<InvoiceFormValues>
                        name={`items.${index}.name`}
                        label={t('invoices.item.name')}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <TextField<InvoiceFormValues>
                        name={`items.${index}.quantity`}
                        label={t('invoices.item.quantity')}
                        required
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <SelectField<InvoiceFormValues>
                        name={`items.${index}.quantityUnit`}
                        label={t('invoices.item.quantityUnit')}
                        options={unitOptions}
                      />
                    </div>
                    <div className="sm:col-span-6">
                      <TextField<InvoiceFormValues>
                        name={`items.${index}.unitPrice`}
                        label={t('invoices.item.unitPrice')}
                        required
                      />
                    </div>
                    <div className="sm:col-span-4">
                      <SelectField<InvoiceFormValues>
                        name={`items.${index}.vatRate`}
                        label={t('invoices.item.vatRate')}
                        disabled={vatLocked}
                        options={vatOptions}
                      />
                    </div>
                    <div className="sm:col-span-8">
                      <TextField<InvoiceFormValues>
                        name={`items.${index}.note`}
                        label={t('invoices.item.note')}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  {/* Élő sor-összegek (a shared kanonikus számítójából) */}
                  <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm tabular-nums">
                    <span data-testid={`item-net-${String(index)}`}>
                      {t('invoices.item.net')}:{' '}
                      {preview ? formatMoney(preview.netPrice, currency) : '—'}
                    </span>
                    <span data-testid={`item-vat-${String(index)}`}>
                      {t('invoices.item.vatAmount')}:{' '}
                      {preview ? formatMoney(preview.vatAmount, currency) : '—'}
                    </span>
                    <span
                      className="text-foreground font-medium"
                      data-testid={`item-gross-${String(index)}`}
                    >
                      {t('invoices.item.gross')}:{' '}
                      {preview ? formatMoney(preview.grossPrice, currency) : '—'}
                    </span>
                  </div>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-2"
                      aria-label={t('invoices.form.removeItem')}
                      onClick={() => remove(index)}
                    >
                      <TrashIcon className="text-destructive h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Fej-összegek (élő) */}
          <div className="ml-auto mt-2 w-full text-sm tabular-nums sm:w-72">
            <Divider spacing="none" inset={false} className="mb-4" />
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('invoices.totals.net')}</span>
                <span data-testid="totals-net">{formatMoney(totals.netTotal, currency)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('invoices.totals.vat')}</span>
                <span data-testid="totals-vat">{formatMoney(totals.vatTotal, currency)}</span>
              </div>
              <div className="flex items-center justify-between text-base font-semibold">
                <span>{t('invoices.totals.gross')}</span>
                <span data-testid="totals-gross">{formatMoney(totals.grossTotal, currency)}</span>
              </div>
            </div>
          </div>
        </FormSection>

        {/* 6. Számla-megjegyzés (EGY mező — a fejléc/lábléc kettős 2026-08-26-án összevonva) */}
        <FormSection title={t('invoices.form.sectionNote')}>
          <TextareaField<InvoiceFormValues>
            name="note"
            label={t('invoices.form.note')}
            hint={t('invoices.form.noteHint')}
            rows={4}
          />
        </FormSection>

        {/* AKCIÓ — JOBBRA (a sáv-/lábléc-szabály), mobilon teljes szélességű. „Vissza" gomb NINCS: a
            vissza-navigáció helye a topbar szülő-morzsája („Számlák / …") — a részletes nézettel egyezően. */}
        <div className="flex justify-end pt-1">
          <Button type="submit" variant="brand" disabled={pending} className="max-sm:w-full">
            {pending ? t('invoices.form.saving') : t('invoices.form.save')}
          </Button>
        </div>
      </form>
    </Form>
  );
}

/**
 * Egyszerű (nem form-kötött) választó a billing-profil-alkalmazáshoz — a form-state-et NEM módosítja
 * közvetlenül, a `onChange` egy PROFIL-BETÖLTŐT hív (`applyProfile`).
 *
 * A GLOBÁLIS `Select`-re épül (nem natív `<select>`): a natív elem kézzel másolt Tailwind-osztályokkal
 * imitálta a design-rendszert, ami elcsúszott volna az első token-változásnál. A profil-lista rövid
 * (ügyfélenként néhány profil), ezért `Select` és nem `EntityCombobox` — kereső itt nem indokolt.
 */
function ProfileSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}): JSX.Element {
  return (
    // `exactOptionalPropertyTypes`: a `value` propot KI kell hagyni (nem `undefined`-et adni), amíg nincs
    // választás — így a Radix a `placeholder`-t mutatja.
    <Select {...(value ? { value } : {})} onValueChange={onChange}>
      <SelectTrigger aria-label={placeholder}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
